import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { firewallService } from '../services/api';

const ipAddressRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
const hostnameRegex = /^[a-zA-Z0-9][a-zA-Z0-9.-]*$/;

const AddFirewallForm = ({ open, onClose, onSuccess }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      device_name: '',
      ip_address: '',
      username: '',
      password: '',
      type: 'unknown',
      protocol: 'SSH',
    },
    validationSchema: Yup.lazy((values) => {
      const isCiscoFtd = values?.type === 'cisco_ftd';
      const isJuniperSrx = values?.type === 'juniper_srx';
      const isPaloAlto = values?.type === 'paloalto_panos';
      return Yup.object({
        device_name: Yup.string()
          .required('Device name is required')
          .min(3, 'Device name must be at least 3 characters'),
        ip_address: Yup.string()
          .required('IP address or hostname is required')
          .test(
            'address-format',
            'Please enter a valid IP address (e.g. 192.168.1.1) or hostname (e.g. ftd.securemango.com)',
            (value) =>
              !value || ipAddressRegex.test(value) || hostnameRegex.test(value)
          ),
        username: Yup.string()
          .required('Username is required')
          .min(3, 'Username must be at least 3 characters'),
        password: Yup.string()
          .required('Password is required')
          .min(8, 'Password must be at least 8 characters'),
        type: Yup.string()
          .required('Firewall type is required')
          .oneOf(['juniper_srx', 'huawei_usg', 'fortinet_fortigate', 'cisco_ftd', 'paloalto_panos', 'checkpoint_security_gateway', 'unknown'], 'Invalid firewall type'),
        protocol: (isCiscoFtd || isJuniperSrx || isPaloAlto)
          ? Yup.string().required('Protocol is required').oneOf(['SSH', 'API'], 'Invalid protocol')
          : Yup.string().oneOf(['SSH', 'API']).default('SSH'),
      });
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      try {
        const payload = { ...values };
        if (payload.type !== 'cisco_ftd' && payload.type !== 'juniper_srx' && payload.type !== 'paloalto_panos') payload.protocol = 'SSH';
        await firewallService.createFirewall(payload);
        onSuccess();
        onClose();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to add firewall');
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          Add New Firewall
        </Typography>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl
              fullWidth
              error={formik.touched.type && Boolean(formik.errors.type)}
            >
              <InputLabel>Device Type</InputLabel>
              <Select
                id="type"
                name="type"
                value={formik.values.type}
                onChange={formik.handleChange}
                label="Device Type"
              >
                <MenuItem value="juniper_srx">Juniper SRX</MenuItem>
                <MenuItem value="huawei_usg">Huawei USG</MenuItem>
                <MenuItem value="fortinet_fortigate">Fortinet FortiGate</MenuItem>
                <MenuItem value="cisco_ftd">Cisco FTD</MenuItem>
                <MenuItem value="paloalto_panos">PaloAlto PAN-OS</MenuItem>
                <MenuItem value="checkpoint_security_gateway">Checkpoint Security Gateway</MenuItem>
                <MenuItem value="unknown">Unknown</MenuItem>
              </Select>
              {formik.touched.type && formik.errors.type && (
                <FormHelperText>{formik.errors.type}</FormHelperText>
              )}
            </FormControl>
            {(formik.values.type === 'cisco_ftd' || formik.values.type === 'juniper_srx' || formik.values.type === 'paloalto_panos') && (
              <FormControl
                fullWidth
                error={formik.touched.protocol && Boolean(formik.errors.protocol)}
              >
                <InputLabel>Protocol</InputLabel>
                <Select
                  id="protocol"
                  name="protocol"
                  value={formik.values.protocol}
                  onChange={formik.handleChange}
                  label="Protocol"
                >
                  <MenuItem value="SSH">SSH</MenuItem>
                  <MenuItem value="API">API</MenuItem>
                </Select>
                {formik.touched.protocol && formik.errors.protocol && (
                  <FormHelperText>{formik.errors.protocol}</FormHelperText>
                )}
              </FormControl>
            )}
            <TextField
              fullWidth
              id="device_name"
              name="device_name"
              label="Device Name"
              value={formik.values.device_name}
              onChange={formik.handleChange}
              error={formik.touched.device_name && Boolean(formik.errors.device_name)}
              helperText={formik.touched.device_name && formik.errors.device_name}
            />
            <TextField
              fullWidth
              id="ip_address"
              name="ip_address"
              label="IP Address"
              value={formik.values.ip_address}
              onChange={formik.handleChange}
              placeholder="e.g. 192.168.1.1 or ftd.securemango.com"
              error={formik.touched.ip_address && Boolean(formik.errors.ip_address)}
              helperText={formik.touched.ip_address ? formik.errors.ip_address : 'Enter the IP address or domain name of the device'}
            />
            <TextField
              fullWidth
              id="username"
              name="username"
              label="Username"
              value={formik.values.username}
              onChange={formik.handleChange}
              error={formik.touched.username && Boolean(formik.errors.username)}
              helperText={formik.touched.username && formik.errors.username}
            />
            <TextField
              fullWidth
              id="password"
              name="password"
              label="Password"
              type="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Adding...' : 'Add Firewall'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddFirewallForm; 